import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Icon } from '@iconify/react'
import { DonorMascot } from '../DonorMascot'
import type { MascotConfig } from '../mascot-catalog'
import type { AvatarConfig } from '../../donorAvatar/avatar-catalog'
import type { ChallengeRules, GameEvent, GameSession } from './mascot-game-types'
import { miniGamesForHabitat, timeLabel } from './mascot-game-view'
import { GameConfirm } from './GameConfirm'
import { FrisbeeGame } from './FrisbeeGame'
import styles from './MascotMiniGame.module.css'

const SYMBOLS = ['🍎', '🌻', '🍀', '🍓', '🐝', '🌈', '🍇', '🦋', '🍄', '🐚', '🌙', '🍋']
const SHEEP_SYMBOLS = [
  '🐑',
  '🌻',
  '☘️',
  '🥕',
  '🐝',
  '🏡',
  '🌾',
  '🦋',
  '🤠',
  '🥬',
  '🌙',
  '🍃',
]
const NOTES = ['Folha', 'Sol', 'Flor', 'Céu']
const NOTE_ICONS = ['🌿', '☀️', '🌷', '☁️']

export type MiniGameProps = {
  session: GameSession
  mascot: MascotConfig
  avatar?: AvatarConfig | null
  onFinish: (events: GameEvent[]) => Promise<boolean>
  onBack: () => void
  onRestart?: () => void
  restartBlockedReason?: string
  restartLabel?: string
  autoStart?: boolean
  playSound: (note?: number) => void
  resultMessage: string
  error: string
  busy: boolean
  wallet?: ReactNode
}

export function MascotMiniGame(props: MiniGameProps) {
  return props.session.kind === 'frisbee' ? (
    <FrisbeeGame {...props} />
  ) : (
    <ClassicMiniGame {...props} />
  )
}

function ClassicMiniGame({
  session,
  mascot,
  onFinish,
  onBack,
  playSound,
  resultMessage,
  error,
  busy,
  wallet,
}: MiniGameProps) {
  const [phase, setPhase] = useState<'ready' | 'playing' | 'result'>('ready')
  const [elapsed, setElapsed] = useState(0)
  const [saved, setSaved] = useState(false)
  const [score, setScore] = useState(0)
  const [confirmLeave, setConfirmLeave] = useState(false)
  const started = useRef(0)
  const events = useRef<GameEvent[]>([])
  const finished = useRef(false)
  const finishRef = useRef(onFinish)
  useEffect(() => {
    finishRef.current = onFinish
  }, [onFinish])
  const game = miniGamesForHabitat(
    mascot.species === 'jaguar' && mascot.backdrop !== 'farm',
    mascot.species === 'sheep'
  ).find((item) => item.id === session.kind)!

  const finish = () => {
    if (finished.current) return
    finished.current = true
    setPhase('result')
    void finishRef.current(events.current).then(setSaved)
  }
  const finishCallback = useRef(finish)
  useEffect(() => {
    finishCallback.current = finish
  })

  useEffect(() => {
    if (phase !== 'playing') return
    let frame = 0
    const tick = () => {
      const passed = performance.now() - started.current
      setElapsed(passed)
      if (passed >= session.duration_ms) finishCallback.current()
      else frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [phase, session.duration_ms])

  const record = (value: number, deadline = session.duration_ms) => {
    const at = Math.floor(performance.now() - started.current)
    if (
      finished.current ||
      at < 90 ||
      at > Math.min(deadline, session.duration_ms) ||
      at - (events.current.at(-1)?.at ?? -1000) < 80 ||
      events.current.length >= 400
    )
      return null
    events.current.push({ at, value })
    return at
  }

  return (
    <section
      className={styles.arena}
      data-phase={phase}
      data-game={session.kind}
      aria-label={game.name}
    >
      <header className={styles.header}>
        <button
          className={styles.back}
          onClick={() =>
            phase === 'playing' || (phase === 'result' && !saved)
              ? setConfirmLeave(true)
              : onBack()
          }
          disabled={busy}
        >
          <Icon icon="mdi:arrow-left" /> Jogos
        </button>
        <span className={styles.title}>
          {game.icon} {game.name}
        </span>
        <div className={styles.headerBalances}>
          <span className={styles.timer} aria-label="Tempo restante">
            {timeLabel((session.duration_ms - elapsed) / 1000)}
          </span>
          {wallet}
        </div>
      </header>
      {phase === 'ready' ? (
        <div className={styles.ready}>
          <span className={styles.bigIcon}>{game.icon}</span>
          <span className={styles.eyebrow}>UMA NOVA AVENTURA</span>
          <h2>{game.name}</h2>
          <p>{game.description}</p>
          <p className={styles.instructions}>
            {session.kind === 'memory'
              ? `Encontre ${session.values.length / 2} pares${session.rules ? ` em até ${session.rules.attempts} tentativas` : ''}. Cada par vale 10 pontos.`
              : session.kind === 'sequence'
                ? `Repita as luzes na ordem. ${session.rules ? 'Comece com três notas e responda antes do tempo acabar.' : 'São oito rodadas.'}`
                : `Use as setas ou toque nas faixas. ${session.kind === 'runner' && session.rules ? 'Duas faixas têm pedras: procure o caminho livre!' : 'Pegue as frutas antes de cinco erros.'} O ritmo acelera.`}
          </p>
          <button
            className={styles.primary}
            onClick={() => {
              started.current = performance.now()
              setPhase('playing')
            }}
            autoFocus
          >
            Vamos jogar <Icon icon="mdi:arrow-right" />
          </button>
          <small>
            {session.rules
              ? `${session.rules.name} · até ${session.rules.max_coins} moedas. Alcance 50% dos pontos para receber moedas e XP. A energia já foi usada ao preparar a partida.`
              : 'As moedas são só para os itens do mascote.'}
          </small>
        </div>
      ) : phase === 'playing' ? (
        <>
          <div className={styles.score}>
            <span>PONTOS</span>
            <strong>{score.toString().padStart(2, '0')}</strong>
          </div>
          {session.kind === 'memory' ? (
            <MemoryGame
              symbols={mascot.species === 'sheep' ? SHEEP_SYMBOLS : SYMBOLS}
              rules={session.rules}
              values={session.values}
              record={record}
              onScore={setScore}
              onEnd={finish}
              sound={playSound}
            />
          ) : session.kind === 'sequence' ? (
            <SequenceGame
              rules={session.rules}
              values={session.values}
              elapsed={elapsed}
              record={record}
              onScore={setScore}
              onEnd={finish}
              sound={playSound}
            />
          ) : (
            <LaneGame
              session={session}
              mascot={mascot}
              elapsed={elapsed}
              record={record}
              onScore={setScore}
              onEnd={finish}
              sound={playSound}
            />
          )}
        </>
      ) : (
        <div className={styles.ready}>
          <span className={styles.bigIcon}>{score > 0 ? '🏆' : '🌱'}</span>
          <span className={styles.eyebrow}>AVENTURA CONCLUÍDA</span>
          <h2>{score > 0 ? 'Mandou muito bem!' : 'Cada partida é um começo.'}</h2>
          <p className={styles.finalScore}>
            {score} <span>pontos</span>
          </p>
          <p role="status">
            {busy
              ? 'Guardando suas recompensas…'
              : saved
                ? resultMessage
                : 'Sua partida terminou. Confirme o resultado para guardar as recompensas.'}
          </p>
          {error && (
            <p role="alert" className={styles.error}>
              {error}
            </p>
          )}
          {!saved && !busy && (
            <button
              className={styles.primary}
              onClick={() => {
                void onFinish(events.current).then(setSaved)
              }}
            >
              Tentar salvar resultado
            </button>
          )}
          <button
            className={saved ? styles.primary : styles.back}
            disabled={busy}
            onClick={() => (saved ? onBack() : setConfirmLeave(true))}
          >
            Voltar aos jogos
          </button>
        </div>
      )}
      {confirmLeave && (
        <GameConfirm
          title="Sair desta partida?"
          description="Uma partida sem resultado salvo não entrega recompensas."
          leaveLabel="Sair da partida"
          onContinue={() => setConfirmLeave(false)}
          onLeave={onBack}
        />
      )}
    </section>
  )
}

function MemoryGame({
  symbols,
  rules,
  values,
  record,
  onScore,
  onEnd,
  sound,
}: {
  symbols: string[]
  rules?: ChallengeRules
  values: number[]
  record: (value: number, deadline?: number) => number | null
  onScore: (score: number) => void
  onEnd: () => void
  sound: (note: number) => void
}) {
  const [open, setOpen] = useState<number[]>([])
  const [matched, setMatched] = useState<number[]>([])
  const [attempts, setAttempts] = useState(0)
  const lock = useRef(false)
  const timer = useRef<number | undefined>(undefined)
  useEffect(() => () => clearTimeout(timer.current), [])
  function flip(index: number) {
    if (
      lock.current ||
      open.includes(index) ||
      matched.includes(index) ||
      record(index) === null
    )
      return
    sound(values[index] % 4)
    if (open.length === 0) {
      setOpen([index])
      return
    }
    const first = open[0]
    const nextAttempt = attempts + 1
    setAttempts(nextAttempt)
    setOpen([first, index])
    if (values[first] === values[index]) {
      const next = [...matched, first, index]
      setMatched(next)
      setOpen([])
      onScore(next.length * 5)
      if (next.length === values.length) onEnd()
    } else {
      lock.current = true
      timer.current = window.setTimeout(() => {
        setOpen([])
        lock.current = false
      }, 750)
    }
    if (rules && nextAttempt >= rules.attempts) onEnd()
  }
  return (
    <>
      <p className={styles.gameHint}>
        Pares: {matched.length / 2}/{values.length / 2}.{' '}
        {rules && `${Math.max(0, rules.attempts - attempts)} tentativas restantes.`}
      </p>
      <div
        className={styles.memory}
        data-expanded={values.length > 12}
        style={
          {
            '--memory-rows': Math.ceil(values.length / 4),
            '--memory-wide-rows': Math.ceil(values.length / 6),
          } as CSSProperties
        }
      >
        {values.map((value, index) => (
          <button
            key={index}
            className={`${styles.card} ${open.includes(index) || matched.includes(index) ? styles.flipped : ''} ${matched.includes(index) ? styles.matched : ''}`}
            onClick={() => flip(index)}
            disabled={matched.includes(index)}
            aria-label={`Carta ${index + 1}${open.includes(index) || matched.includes(index) ? `: ${symbols[value]}` : ', virada para baixo'}`}
          >
            <span>
              {open.includes(index) || matched.includes(index) ? symbols[value] : '✿'}
            </span>
          </button>
        ))}
      </div>
    </>
  )
}

function SequenceGame({
  rules,
  values,
  elapsed,
  record,
  onScore,
  onEnd,
  sound,
}: {
  rules?: ChallengeRules
  values: number[]
  elapsed: number
  record: (value: number, deadline?: number) => number | null
  onScore: (score: number) => void
  onEnd: () => void
  sound: (note: number) => void
}) {
  const [round, setRound] = useState(rules ? 3 : 1)
  const [position, setPosition] = useState(0)
  const [roundStart, setRoundStart] = useState(0)
  const [pressed, setPressed] = useState<number | null>(null)
  const flashTimer = useRef<number | undefined>(undefined)
  const relative = elapsed - roundStart - 450
  const speed = rules?.light_ms ?? 650
  const displayIndex = Math.floor(relative / speed)
  const watching = relative < round * speed + 400
  const responseLeft = rules
    ? Math.max(0, rules.response_ms - (relative - round * speed - 400))
    : Infinity
  useEffect(() => {
    if (rules && !watching && responseLeft === 0) onEnd()
  }, [rules, watching, responseLeft, onEnd])
  const light =
    watching &&
    displayIndex >= 0 &&
    displayIndex < round &&
    relative % speed < speed * 0.72
      ? values[displayIndex]
      : null
  useEffect(() => {
    if (light !== null) sound(light)
  }, [light, sound])
  useEffect(() => () => clearTimeout(flashTimer.current), [])
  function answer(value: number) {
    if (watching || responseLeft === 0) return
    const at = record(
      value,
      rules ? roundStart + 450 + round * speed + 400 + rules.response_ms : undefined
    )
    if (at === null) return
    sound(value)
    setPressed(value)
    clearTimeout(flashTimer.current)
    flashTimer.current = window.setTimeout(() => setPressed(null), 150)
    if (value !== values[position]) {
      onEnd()
      return
    }
    if (position + 1 === round) {
      onScore((round - (rules ? 2 : 0)) * 8)
      if (round === values.length) {
        onEnd()
        return
      }
      setRound(round + 1)
      setPosition(0)
      setRoundStart(at + 200)
    } else setPosition(position + 1)
  }
  return (
    <>
      <p className={styles.gameHint} role="status">
        Sequência {round}/{values.length} ·{' '}
        {watching
          ? 'Observe a sequência…'
          : `Sua vez! ${position}/${round}${rules ? ` · ${(responseLeft / 1000).toFixed(1)}s` : ''}`}
      </p>
      <div className={styles.sequence}>
        {NOTES.map((note, index) => (
          <button
            key={note}
            className={`${styles.note} ${light === index || pressed === index ? styles.lit : ''}`}
            data-note={index}
            disabled={watching}
            onClick={() => answer(index)}
            aria-label={note}
          >
            <span>{NOTE_ICONS[index]}</span>
            <strong>{note}</strong>
            <small>{index + 1}</small>
          </button>
        ))}
      </div>
    </>
  )
}

function LaneGame({
  session,
  mascot,
  elapsed,
  record,
  onScore,
  onEnd,
  sound,
}: {
  session: GameSession
  mascot: MascotConfig
  elapsed: number
  record: (value: number, deadline?: number) => number | null
  onScore: (score: number) => void
  onEnd: () => void
  sound: (note: number) => void
}) {
  const [lane, setLane] = useState(1)
  const maxLives = session.rules && session.kind === 'catch' ? 5 : 3
  const [lives, setLives] = useState(maxLives)
  const [feedback, setFeedback] = useState('')
  const laneRef = useRef(1)
  const processed = useRef(0)
  const points = useRef(0)
  const livesRef = useRef(maxLives)
  const moves = useRef<GameEvent[]>([])
  const runner = session.kind === 'runner'
  const moveRef = useRef<(lane: number) => void>(() => {})
  const move = (next: number) => {
    next = Math.max(0, Math.min(2, next))
    if (next === laneRef.current) return
    const at = record(next)
    if (at === null) return
    moves.current.push({ at, value: next })
    laneRef.current = next
    setLane(next)
  }
  useEffect(() => {
    moveRef.current = move
  })
  useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return
      event.preventDefault()
      moveRef.current(laneRef.current + (event.key === 'ArrowRight' ? 1 : -1))
    }
    window.addEventListener('keydown', keydown)
    return () => window.removeEventListener('keydown', keydown)
  }, [])
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      while (
        processed.current < session.values.length &&
        elapsed >=
          (session.encounters?.[processed.current] ?? (processed.current + 1) * 1200)
      ) {
        const collisionAt =
          session.encounters?.[processed.current] ?? (processed.current + 1) * 1200
        const laneAtCollision =
          [...moves.current].reverse().find((event) => event.at <= collisionAt)?.value ??
          1
        const target = session.values[processed.current++]
        const success =
          runner && !session.rules
            ? laneAtCollision !== target
            : laneAtCollision === target
        if (success) {
          points.current += 3
          sound(2)
          setFeedback('+3 ✦')
        } else if (runner || session.rules) {
          livesRef.current--
          setLives(livesRef.current)
          setFeedback(runner ? 'Ops! Uma pedrinha.' : 'Uma fruta escapou!')
        } else setFeedback('Quase! A próxima é sua.')
        onScore(points.current)
        if (livesRef.current === 0) {
          onEnd()
          break
        }
      }
    })
    return () => cancelAnimationFrame(frame)
  }, [
    elapsed,
    onEnd,
    onScore,
    runner,
    session.values,
    session.rules,
    session.encounters,
    sound,
  ])
  return (
    <>
      <div className={styles.gameHint}>
        {runner || session.rules ? (
          <span aria-label={`${lives} vidas`}>
            {'❤️'.repeat(lives)}
            {'🤍'.repeat(maxLives - lives)}
          </span>
        ) : (
          'Pegue as frutas antes de chegarem ao chão!'
        )}{' '}
        <span>{feedback}</span>
      </div>
      <div
        className={`${styles.lanes} ${runner ? styles.runner : ''}`}
        aria-label="Pista com três faixas"
      >
        {[0, 1, 2].map((index) => (
          <button
            key={index}
            className={styles.lane}
            onPointerDown={() => move(index)}
            onClick={() => move(index)}
            aria-label={`Mover para faixa ${index + 1}`}
            aria-pressed={lane === index}
          />
        ))}
        {session.values.map((target, index) => {
          const at = session.encounters?.[index] ?? (index + 1) * 1200
          const fallDuration = session.rules
            ? Math.max(500, session.rules.interval_ms * (1 - (0.25 * at) / 45000))
            : 1200
          const progress = (elapsed - (at - fallDuration)) / fallDuration
          if (progress < 0 || progress > 1) return null
          const lanes =
            runner && session.rules
              ? [0, 1, 2].filter((lane) => lane !== target)
              : [target]
          return lanes.map((fallingLane) => (
            <span
              key={`${index}-${fallingLane}`}
              className={styles.falling}
              style={{ '--lane': fallingLane, '--fall': progress } as CSSProperties}
              aria-hidden
            >
              {runner
                ? '🪨'
                : mascot.species === 'jaguar'
                  ? '🥩'
                  : mascot.species === 'sheep'
                    ? index % 2 === 0
                      ? '🥕'
                      : '🥬'
                    : SYMBOLS[index % 2 === 0 ? 0 : 3]}
            </span>
          ))
        })}
        <div
          className={styles.basket}
          style={{ '--lane': lane } as CSSProperties}
          aria-hidden
        >
          {runner ? <DonorMascot config={mascot} showBackdrop={false} /> : '🧺'}
        </div>
      </div>
      <p className={styles.controls}>← → no teclado · ou toque na faixa desejada</p>
    </>
  )
}
